import { useState, useEffect, useRef, useCallback } from 'react';
import { MapLibreMap, MapMarker } from '@molecules/index';
import { Input, Card, Button } from '@atoms/index';
import { geocodingService, type GeocodingResult } from '@services/geocodingService';
import { matchTurkishCity } from '@/data/turkish-cities';
import type { Map } from 'maplibre-gl';

export interface GuestDeliveryAddress {
  fullAddress: string;
  city: string;
  district: string;
  latitude: number;
  longitude: number;
  buildingInfo: string;
}

interface GuestDeliveryAddressSectionProps {
  value: GuestDeliveryAddress;
  onChange: (value: GuestDeliveryAddress) => void;
}

const DEFAULT_CENTER: [number, number] = [35.3308, 36.9914];

export function isValidDeliveryCoordinate(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180 &&
    !(latitude === 0 && longitude === 0)
  );
}

const emptyAddress = (): GuestDeliveryAddress => ({
  fullAddress: '',
  city: '',
  district: '',
  latitude: 0,
  longitude: 0,
  buildingInfo: '',
});

export const GuestDeliveryAddressSection = ({
  value,
  onChange,
}: GuestDeliveryAddressSectionProps) => {
  const [mapPosition, setMapPosition] = useState<[number, number]>(DEFAULT_CENTER);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const hasValidCoordinates = isValidDeliveryCoordinate(value.latitude, value.longitude);

  useEffect(() => {
    if (hasValidCoordinates) {
      setMapPosition([value.longitude, value.latitude]);
    }
  }, [value.latitude, value.longitude, hasValidCoordinates]);

  useEffect(() => {
    setIsMapReady(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyCoordinates = useCallback(
    async (latitude: number, longitude: number, preserveAddressText = false) => {
      if (!isValidDeliveryCoordinate(latitude, longitude)) {
        return;
      }

      setMapPosition([longitude, latitude]);
      setAddressError(null);
      setIsReverseGeocoding(true);

      try {
        const result = await geocodingService.reverseGeocode(latitude, longitude);
        if (result) {
          const matchedCity = matchTurkishCity(result.city || '');
          onChange({
            ...value,
            fullAddress: preserveAddressText && value.fullAddress
              ? value.fullAddress
              : result.fullAddress || value.fullAddress,
            city: matchedCity || result.city || value.city,
            district: result.district || value.district,
            latitude,
            longitude,
          });
          if (!preserveAddressText) {
            setSearchQuery(result.fullAddress || '');
          }
        } else {
          onChange({ ...value, latitude, longitude });
        }
      } finally {
        setIsReverseGeocoding(false);
      }
    },
    [onChange, value],
  );

  const handleMapClick = useCallback(
    (lng: number, lat: number) => {
      void applyCoordinates(lat, lng);
    },
    [applyCoordinates],
  );

  const handleMarkerDragEnd = useCallback(
    (position: [number, number]) => {
      const [lng, lat] = position;
      void applyCoordinates(lat, lng);
    },
    [applyCoordinates],
  );

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      setAddressError(null);
      try {
        const results = await geocodingService.searchAddresses(searchQuery);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        if (results.length === 0) {
          setAddressError('Adres bulunamadı. Farklı bir arama deneyin veya haritadan seçin.');
        }
      } catch {
        setSuggestions([]);
        setAddressError('Adres araması yapılamadı. Haritadan konum seçebilirsiniz.');
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const selectSuggestion = (result: GeocodingResult) => {
    if (!isValidDeliveryCoordinate(result.latitude, result.longitude)) {
      setAddressError('Seçilen adresin konum bilgisi geçersiz.');
      return;
    }

    const matchedCity = matchTurkishCity(result.city || '');
    onChange({
      ...value,
      fullAddress: result.fullAddress,
      city: matchedCity || result.city || '',
      district: result.district || '',
      latitude: result.latitude,
      longitude: result.longitude,
    });
    setMapPosition([result.longitude, result.latitude]);
    setSearchQuery(result.fullAddress);
    setShowSuggestions(false);
    setAddressError(null);
    mapInstance?.flyTo({ center: [result.longitude, result.latitude], zoom: 16 });
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setAddressError('Tarayıcınız konum özelliğini desteklemiyor.');
      return;
    }

    setAddressError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void applyCoordinates(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        setAddressError('Konum alınamadı. Lütfen adres arayın veya haritadan seçin.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative" ref={suggestionsRef}>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input
              label="Adres Ara"
              placeholder="Mahalle, sokak, bina no..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setAddressError(null);
              }}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              autoComplete="street-address"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleUseMyLocation}
            className="shrink-0 mb-0.5"
          >
            Konumum
          </Button>
        </div>
        {searchQuery.length > 0 && searchQuery.length < 3 && (
          <p className="text-xs text-gray-500 mt-1">En az 3 karakter yazın</p>
        )}
        {(isSearching || isReverseGeocoding) && (
          <p className="text-xs text-primary mt-1">Konum güncelleniyor...</p>
        )}
        {addressError && (
          <p className="text-xs text-amber-700 mt-1">{addressError}</p>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <Card className="absolute z-20 w-full mt-1 max-h-52 overflow-y-auto shadow-lg border border-gray-100">
            <ul>
              {suggestions.map((item, index) => (
                <li key={`${item.latitude}-${item.longitude}-${index}`}>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm border-b border-gray-50 last:border-0"
                    onClick={() => selectSuggestion(item)}
                  >
                    <span className="font-medium text-gray-900 block truncate">
                      {item.displayName || item.fullAddress}
                    </span>
                    {(item.district || item.city) && (
                      <span className="text-xs text-gray-500">
                        {[item.district, item.city].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <div className="h-72 sm:h-80 rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 relative">
        {isMapReady && mapPosition ? (
          <MapLibreMap
            center={mapPosition}
            zoom={hasValidCoordinates ? 16 : 12}
            onMapLoad={setMapInstance}
            onMapClick={handleMapClick}
            className="w-full h-full"
          >
            {hasValidCoordinates && (
              <MapMarker
                position={[value.longitude, value.latitude]}
                iconType="delivery"
                size={36}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </MapLibreMap>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
            Harita yükleniyor...
          </div>
        )}
        {hasValidCoordinates && (
          <div className="absolute bottom-3 left-3 right-3 sm:right-auto bg-white/95 backdrop-blur-sm px-3 py-2 rounded-xl shadow-md text-xs pointer-events-none">
            <p className="font-medium text-gray-900">Teslimat noktası seçildi</p>
            <p className="text-gray-600 truncate">
              {value.fullAddress || `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}`}
            </p>
            <p className="text-gray-400 mt-0.5">İnce ayar için pini sürükleyebilirsiniz</p>
          </div>
        )}
        {!hasValidCoordinates && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center px-4 pointer-events-none">
            <p className="text-sm text-gray-600 bg-white/90 inline-block px-3 py-1.5 rounded-lg shadow-sm">
              Haritaya dokunarak teslimat noktanızı işaretleyin
            </p>
          </div>
        )}
      </div>

      <Input
        label="Açık Adres"
        placeholder="Mahalle, sokak, bina no, daire..."
        value={value.fullAddress}
        onChange={(e) => onChange({ ...value, fullAddress: e.target.value })}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="İl"
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
          required
        />
        <Input
          label="İlçe"
          value={value.district}
          onChange={(e) => onChange({ ...value, district: e.target.value })}
        />
      </div>

      <Input
        label="Bina / Daire / Kat (isteğe bağlı)"
        placeholder="Örn: A Blok, Daire 5, Zemin kat"
        value={value.buildingInfo}
        onChange={(e) => onChange({ ...value, buildingInfo: e.target.value })}
      />
    </div>
  );
};

export const createEmptyGuestDeliveryAddress = emptyAddress;
